import { Controller, useForm } from 'react-hook-form';
import { Label, LabelSync } from '../../shared/types';
import { useNavigate } from 'react-router-dom';
import {
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { Messages } from '../../../../../utils/message-constants';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { handleErrors } from '../../../../../utils/errors.ts';
import axios from 'axios';
import { ApiException } from '../../../../../../types.ts';
import SharedTransferList from '../../../../../shared/shared-transfer-list.tsx';
import {
  getContactSyncsList,
  getDomains,
  UserKeys,
} from '../../../../../api/users-api.ts';
import { GPanelContactsKeys } from '../../gpanel-contacts-api.ts';
import SimpleBreadcrumbsPage from '../../../../../shared/simple-breadcrumbs-page';
import { pagesConfig } from '../../../../../shared/pages-config';
import LabelsAutocomplete from '../../../../../shared/labels-autocomplete.tsx';
import { useLabelsQuery } from '../../gpanel-contacts-queries.ts';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const pathToBack_fromEdit = '../..';
const pathToBack_fromCreate = '..';

interface GpanelContactsSyncFormProps {
  title: string;
  caption?: string;
  submitText: string;
  label?: Pick<Label, 'id' | 'name'>;
  setNewLabel?: React.Dispatch<React.SetStateAction<number | undefined>>;
  mutation: UseMutationResult<
    LabelSync,
    Error,
    {
      sync: Partial<LabelSync>;
      changeLogMessage?: string;
    },
    unknown
  >;
  sync?: LabelSync;
}

export default function GpanelContactsSyncForm({
  title,
  caption,
  submitText,
  label,
  setNewLabel,
  sync,
  mutation,
}: GpanelContactsSyncFormProps) {
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { createEvent } = useAnalytics();
  const { changeLogRoles } = useUserSettings();
  const setSnackbar = useStore((state) => state.setSnackbar);
  const { data: gpanelLabels = [] } = useLabelsQuery();
  const navigate = useNavigate();
  const isEdit = Boolean(sync);
  const goBack = (path?: string) =>
    navigate(path ?? pathToBack_fromCreate, { relative: 'path' });
  const { data: domains } = useQuery({
    queryKey: [UserKeys.Domains],
    queryFn: () => getDomains(),
    meta: {
      errorMessage:
        'There was an issue loading domain data. Please refresh and try again.',
    },
  });

  const { data: contactSyncs, isLoading } = useQuery({
    queryKey: [UserKeys.ContactSyncs],
    queryFn: () => getContactSyncsList(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contact syncs'),
    },
  });

  const getDefaultValues = (sync?: Partial<LabelSync>): Partial<LabelSync> => {
    if (sync) {
      const gpanelLabel = gpanelLabels.find(
        (label) => label.id === sync.labelId
      );

      return {
        ...sync,
        sourceLabel: gpanelLabel ? gpanelLabel.name : '',
      };
    }
    return {
      name: '',
      description: '',
      sourceLabel: label?.name ? label.name : '',
      targetLabelName: label?.name ? label.name : '',
      targetType: 'User',
      targets: [],
      labelId: label?.id ? label.id : -1,
      addToMyContacts: true,
      allSource: false,
    };
  };

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<Partial<LabelSync>>({
    defaultValues: getDefaultValues(sync),
    reValidateMode: 'onChange',
  });

  const targets = watch('targets');
  const targetType = watch('targetType');
  const allSource = watch('allSource');

  const onSubmit = async ({
    sourceLabel: _sourceLabel,
    ...data
  }: Partial<LabelSync>) => {
    if (
      !openChangeLogDialog &&
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.CreateContactSync.value
      )
    ) {
      setOpenChangeLogDialog(true);
      return;
    }

    if (contactSyncs?.some((s) => s.targetGroupName === data.targetLabelName)) {
      setError('targetLabelName', {
        message:
          'Label name already exists as a a target in a legacy Contact Sync, name must be unique.',
      });
      return;
    }

    if (!isDirty) return;

    mutation.mutate(
      { sync: data, changeLogMessage },
      {
        onSuccess: (result) => {
          setChangeLogMessage('');
          setSnackbar({
            open: true,
            severity: 'success',
            message: isEdit
              ? Messages.Success.Updated('Sync')
              : Messages.Success.Added('Sync'),
          });

          queryClient.invalidateQueries({
            queryKey: [GPanelContactsKeys.AllSyncs],
          });
          queryClient.invalidateQueries({
            queryKey: [GPanelContactsKeys.LabelSyncs, String(result.labelId)],
          });
          queryClient.invalidateQueries({
            queryKey: [GPanelContactsKeys.LabelGroups],
          });
          if (isEdit) {
            queryClient.invalidateQueries({
              queryKey: [GPanelContactsKeys.SyncById, String(sync?.id)],
            });
          }

          goBack(isEdit ? pathToBack_fromEdit : pathToBack_fromCreate);
        },
        onError: (error) => {
          if (axios.isAxiosError(error)) {
            const errorResponse = error.response?.data as ApiException;
            if (
              errorResponse.message ===
              'Label name already exists, name must be unique.'
            ) {
              setError('targetLabelName', { message: errorResponse.message });
              return;
            }
          }

          handleErrors({
            error,
            snackbarMessage: isEdit
              ? Messages.Errors.Network.Updating('sync')
              : Messages.Errors.Network.Adding('sync'),
          });
        },
        onSettled: () => {
          setOpenChangeLogDialog(false);
          createEvent(
            isEdit
              ? analytics.directory.gpanel_contacts.update_sync
              : analytics.directory.gpanel_contacts.create_sync,
            {
              module: ModuleList.modules.directory.name,
              section: ModuleList.modules.directory.sections.gpanelContacts,
            }
          );
        },
      }
    );
  };

  function addSelection(values: string[]) {
    const uniqueTargets = Array.from(new Set([...targets!, ...values]));
    setValue('targets', uniqueTargets, { shouldDirty: true });
    clearErrors();
    if (uniqueTargets.length === 0) {
      trigger('targets');
    }
  }

  function removeSelection(values: string[]) {
    const newList = targets!.filter((target) => !values.includes(target));
    setValue('targets', newList, { shouldDirty: true });
  }

  function handleAddAllChecked(value: boolean) {
    setValue('allSource', value);
    if (value === true) {
      setValue('targets', [], { shouldDirty: true });
      clearErrors();
    }
  }

  return (
    <>
      <SimpleBreadcrumbsPage
        isSubPage
        breadcrumbs={[
          { label: ModuleList.modules.directory.name },
          {
            label: pagesConfig.gpanelContacts.name,
            path: pagesConfig.gpanelContacts.link,
          },
        ]}
      >
        <Stack
          flexDirection="column"
          gap={1}
          sx={{
            minHeight: 420,
            overflow: 'hidden',
            flexGrow: 1,
            height: 'calc(100vh - 150px)',
          }}
        >
          <Card
            sx={{
              width: '100%',
              minHeight: 400,
              overflow: 'auto',
              display: 'flex',
              height: '100%',
            }}
          >
            <Stack
              flexDirection="column"
              justifyContent="start"
              alignItems="start"
              sx={{
                height: '100%',
                overflow: 'hidden',
                flexGrow: 1,
                pr: 1,
              }}
            >
              <DialogTitle
                sx={{
                  position: 'relative',
                  alignSelf: 'center',
                  mt: 2,
                }}
                whiteSpace={'nowrap'}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
              >
                {title}
                <Box
                  sx={{
                    width: '96px',
                    height: '2px',
                    backgroundColor: 'primary.main',
                    mx: 'auto',
                  }}
                ></Box>
              </DialogTitle>
              {caption && (
                <Typography sx={{ pt: 1, ml: 4, mb: 2 }}>{caption}</Typography>
              )}
              <Stack
                direction="column"
                sx={{ width: '100%', overflowY: 'auto', flexGrow: 1 }}
              >
                <Container
                  sx={{
                    // ml: 7,
                    pt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 0,
                  }}
                >
                  <Grid container spacing={0.5} sx={{ ml: 7 }}>
                    <Grid size={5}>
                      <Controller
                        name="name"
                        rules={{ required: 'Contact Sync Name is required' }}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            required
                            autoComplete="off"
                            slotProps={{
                              input: {
                                id: 'name',
                              },
                              inputLabel: {
                                htmlFor: 'name',
                              },
                            }}
                            label="Contact Sync Name"
                            sx={{ width: '100%', maxWidth: 400 }}
                            {...field}
                            error={Boolean(errors?.name)}
                            helperText={
                              errors?.name
                                ? 'Contact Sync Name is required.'
                                : ' '
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={5}>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            autoComplete="off"
                            slotProps={{
                              input: {
                                id: 'description',
                              },
                              inputLabel: {
                                htmlFor: 'description',
                              },
                            }}
                            label="Sync Description"
                            sx={{ width: '100%', maxWidth: 400 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={10}>
                      <Controller
                        name="sourceLabel"
                        rules={{ required: 'Source Label is required' }}
                        control={control}
                        render={({ field }) => (
                          <LabelsAutocomplete
                            value={field.value ?? ''}
                            name={field.name}
                            setValue={setValue}
                            setSelectedLabelId={(id) => setValue('labelId', id)}
                            setNewLabelId={setNewLabel}
                            error={!!errors.sourceLabel}
                            onChange={(val) => {
                              field.onChange(val);
                              setValue('targetLabelName', val);
                              clearErrors('targetLabelName');
                            }}
                            disableOnEdit={isEdit}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={5}>
                      <Controller
                        name="targetLabelName"
                        rules={{ required: 'Google Label Name is required.' }}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            autoComplete="off"
                            slotProps={{
                              input: {
                                id: 'targetLabelName',
                              },
                              inputLabel: {
                                htmlFor: 'targetLabelName',
                              },
                            }}
                            error={!!errors.targetLabelName}
                            helperText={
                              errors?.targetLabelName?.message ||
                              `This label will appear in the target's Google Contacts. It defaults to the source label name but can be customized. The label name must be unique.`
                            }
                            label="Google Label Name"
                            sx={{ width: '100%', maxWidth: 400 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={5}>
                      <Controller
                        name="addToMyContacts"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value}
                                onChange={(e) =>
                                  field.onChange(e.target.checked)
                                }
                                slotProps={{
                                  input: {
                                    id: 'addToMyContacts',
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {`Add to “My Contacts”`}
                              </Typography>
                            }
                            sx={{ ml: 0.5 }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Stack
                    sx={{
                      width: '100%',
                      overflow: 'hidden',
                      flexGrow: 1,
                      mt: 3,
                    }}
                  >
                    <Stack
                      flexDirection={'row'}
                      gap={4}
                      alignItems={'center'}
                      justifyContent="center"
                    >
                      <Controller
                        name={`targetType`}
                        control={control}
                        render={({ field: props }) => {
                          return (
                            <FormControl>
                              <Stack
                                sx={{ width: '100%', textAlign: 'center' }}
                              >
                                <Typography>Targets</Typography>
                                <Typography variant="body2">
                                  Choose the users, groups, or domains who will
                                  receive these synced contacts:
                                </Typography>
                              </Stack>
                              <RadioGroup
                                aria-labelledby="radio-buttons-group-label"
                                defaultValue="User"
                                name="radio-buttons-group"
                                value={props.value}
                                onChange={(e) => {
                                  props.onChange(e.target.value);
                                  clearErrors();
                                }}
                                row
                                sx={{ justifyContent: 'center' }}
                              >
                                <FormControlLabel
                                  value="User"
                                  control={<Radio />}
                                  label="User"
                                  disabled={
                                    targets!.length > 0 || allSource === true
                                  }
                                />
                                <FormControlLabel
                                  value="Group"
                                  control={<Radio />}
                                  label="Group"
                                  disabled={
                                    targets!.length > 0 || allSource === true
                                  }
                                />
                                <FormControlLabel
                                  value="OrgUnit"
                                  control={<Radio />}
                                  label="Org Unit"
                                  disabled={
                                    targets!.length > 0 || allSource === true
                                  }
                                />
                                <FormControlLabel
                                  value="Domain"
                                  control={<Radio />}
                                  label="Domain"
                                  disabled={
                                    targets!.length > 0 || allSource === true
                                  }
                                />
                              </RadioGroup>
                            </FormControl>
                          );
                        }}
                      />
                    </Stack>
                    <Stack flexDirection={'row'} gap={1}>
                      <Controller
                        rules={{
                          required: {
                            value: !allSource,
                            message: 'Target is required.',
                          },
                        }}
                        name={'targets'}
                        control={control}
                        render={() => (
                          <Box
                            sx={{
                              height: '40vh',
                              width: '100%',
                            }}
                          >
                            <SharedTransferList
                              sources={targets}
                              addSelection={addSelection}
                              removeSelection={removeSelection}
                              handleAddAllChecked={handleAddAllChecked}
                              errors={!!errors.targets}
                              helperText={errors?.targets?.message || ' '}
                              allSource={allSource}
                              domains={domains || []}
                              sourceType={targetType}
                              hasSelectAllOption={true}
                            />
                          </Box>
                        )}
                      />
                    </Stack>
                  </Stack>
                </Container>
              </Stack>
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                sx={{ width: '100%', p: 4, mt: 'auto', flexBase: 0 }}
                alignSelf="end"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    goBack(isEdit ? pathToBack_fromEdit : pathToBack_fromCreate)
                  }
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading}
                >
                  {mutation.isPending ? (
                    <Skeleton variant="circular" width={20} height={20} />
                  ) : (
                    submitText
                  )}
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </SimpleBreadcrumbsPage>
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={true}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={handleSubmit(onSubmit)}
          mutateLoading={mutation.isPending}
        />
      )}
    </>
  );
}
